import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/UI/Button";
import { emailVerificationApiService } from "../../infrastructure/api/email-verification-api-service";
import { useAuthStore } from "../stores/auth-store";

const RESEND_COOLDOWN_SECONDS = 60;

export const EmailVerificationRequiredPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [maskedEmail, setMaskedEmail] = useState<string | null>(null);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    // 이메일 인증 상태 확인
    const checkStatus = async () => {
      try {
        const status = await emailVerificationApiService.getVerificationStatus();
        setMaskedEmail(status.maskedEmail || null);

        // 이미 인증된 경우 메인으로 리다이렉트
        if (status.emailVerified) {
          navigate("/", { replace: true });
        }
      } catch (err) {
        // 인증 상태 조회 실패 시 무시
      }
    };

    checkStatus();
  }, [navigate]);

  useEffect(() => {
    // 쿨다운 타이머
    if (cooldown > 0) {
      const timer = setTimeout(() => setCooldown(cooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [cooldown]);

  const handleResend = async () => {
    if (cooldown > 0) return;

    setIsLoading(true);
    setError(null);

    try {
      await emailVerificationApiService.resendVerificationEmail();
      setIsSuccess(true);
      setCooldown(RESEND_COOLDOWN_SECONDS);
    } catch (err: any) {
      setError(err.message || "이메일 발송 중 오류가 발생했습니다.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/", { replace: true });
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full bg-white rounded-[12px] shadow-lg p-8">
        <div className="text-center">
          <div className="w-16 h-16 bg-[#E8EFFF] rounded-full flex items-center justify-center mx-auto mb-6">
            <svg
              className="w-8 h-8 text-[#4285F4]"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
              />
            </svg>
          </div>

          <h2 className="text-xl font-semibold text-[#222] mb-3">
            이메일 인증이 필요합니다
          </h2>

          <p className="text-[#555] mb-4">
            서비스를 이용하려면 이메일 인증이 필요합니다.
            <br />
            {maskedEmail && (
              <span className="font-medium text-[#222]">{maskedEmail}</span>
            )}
            로 발송된 인증 링크를 클릭해 주세요.
          </p>

          {error && (
            <div className="bg-[#EBF3FF] border border-[#4285F4]/20 rounded-[8px] p-3 mb-4">
              <p className="text-sm text-[#4285F4]">{error}</p>
            </div>
          )}

          {isSuccess && (
            <div className="bg-[#E8EFFF] border border-[#4285F4]/20 rounded-[8px] p-3 mb-4">
              <p className="text-sm text-[#4285F4]">
                인증 이메일이 재발송되었습니다.
              </p>
            </div>
          )}

          <div className="bg-[#F5F5F5] rounded-[8px] p-4 mb-6">
            <p className="text-sm text-[#888]">
              • 이메일이 도착하지 않았다면 스팸함을 확인해 주세요.
              <br />• 인증 링크는 30분간 유효합니다.
            </p>
          </div>

          <div className="space-y-3">
            <Button
              type="button"
              onClick={handleResend}
              loading={isLoading}
              disabled={cooldown > 0}
              className="w-full bg-[#4285F4] hover:bg-[#3367D6] text-white font-semibold disabled:bg-[#BBBBBB]"
              style={{ height: "48px", borderRadius: "8px" }}
            >
              {cooldown > 0
                ? `재발송 가능 (${cooldown}초)`
                : "인증 이메일 재발송"}
            </Button>

            <Button
              type="button"
              onClick={handleLogout}
              className="w-full bg-white text-[#222] border border-[#222] hover:bg-[#f5f5f5]"
              style={{ height: "48px", borderRadius: "8px" }}
            >
              로그아웃
            </Button>
          </div>

          <p className="mt-6 text-xs text-[#888]">
            다른 이메일로 가입하셨나요?{" "}
            <button
              onClick={handleLogout}
              className="text-[#4285F4] hover:underline"
            >
              다른 계정으로 로그인
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default EmailVerificationRequiredPage;
